import { useState } from "react";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { usePageMeta } from "@/hooks/usePageMeta";

const SITE = process.env.REACT_APP_LIVE_URL;
const SIZES = { card: { label: "Compact card", w: "300", h: "360" }, banner: { label: "Wide banner", w: "100%", h: "220" } };

const snippet = (size, style) => {
  const { w, h } = SIZES[size];
  return `<iframe src="${SITE}/api/embed/breathe?size=${size}&style=${style}" width="${w}" height="${h}" style="border:0;border-radius:20px;max-width:100%" title="Breathe with me: box breathing by Mindful Me" loading="lazy"></iframe>`;
};

const Option = ({ on, onClick, children, testId }) => (
  <button onClick={onClick} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${on ? "bg-mm-primary text-white" : "bg-mm-primary/10 text-mm-primary"}`} data-testid={testId}>{children}</button>
);

export default function Embed() {
  usePageMeta({ title: "Free box breathing widget for your website | Mindful Me",
    description: "Add a free 'Breathe with me' box breathing widget to your blog, practice or school website. Copy one line of code. No tracking, no cookies.", path: "/embed" });
  const [size, setSize] = useState("card");
  const [style, setStyle] = useState("light");
  const code = snippet(size, style);
  const src = `${process.env.REACT_APP_BACKEND_URL}/api/embed/breathe?size=${size}&style=${style}&preview=true`;
  const copy = () => navigator.clipboard.writeText(code).then(() => toast.success("Code copied. Paste it into your site's HTML 💜")).catch(() => toast.error("Couldn't copy. Select the code and copy it manually"));
  return (
    <PublicLayout>
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-12 pb-20" data-testid="embed-page">
        <span className="mm-chip !cursor-default !text-xs">Free · For bloggers, counsellors & schools</span>
        <h1 className="text-4xl sm:text-5xl font-bold mt-4">Add “Breathe with me” to your site</h1>
        <p className="text-mm-muted mt-3 max-w-2xl">A gentle 1-minute box breathing widget your readers can use right on your page. Copy one line of code. No cookies, no tracking, no ads.</p>
        <div className="grid lg:grid-cols-2 gap-8 mt-10">
          <div>
            <h2 className="font-bold">1. Choose a size</h2>
            <div className="flex gap-2 mt-2">{Object.entries(SIZES).map(([k, v]) => <Option key={k} on={size === k} onClick={() => setSize(k)} testId={`embed-size-${k}`}>{v.label}</Option>)}</div>
            <h2 className="font-bold mt-6">2. Choose a style</h2>
            <div className="flex gap-2 mt-2">{["light", "dark"].map((k) => <Option key={k} on={style === k} onClick={() => setStyle(k)} testId={`embed-style-${k}`}>{k === "light" ? "Light" : "Dark"}</Option>)}</div>
            <h2 className="font-bold mt-6">3. Copy the code</h2>
            <pre className="mt-2 rounded-2xl bg-[#2E2546] text-[#F3EEFC] p-4 text-xs whitespace-pre-wrap break-all" data-testid="embed-code">{code}</pre>
            <button onClick={copy} className="mm-btn mt-3" data-testid="embed-copy"><Copy size={16} />Copy code</button>
            <p className="text-xs text-mm-muted mt-4">Paste it into any page that accepts HTML (WordPress “Custom HTML” block, Squarespace “Code” block, Wix “Embed code”). The widget links back to our free calm tools.</p>
          </div>
          <div>
            <h2 className="font-bold">Live preview</h2>
            <div className="mt-2 rounded-3xl border border-dashed border-mm-primary/30 p-4 flex justify-center bg-white" data-testid="embed-preview">
              <iframe key={src} src={src} width={SIZES[size].w} height={SIZES[size].h} style={{ border: 0, borderRadius: 20, maxWidth: "100%" }} title="Breathe with me preview" />
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
